/**
 * "Ungroup" — detach variants from their parent. On the reference list the
 * selection is parents, so their variants are loaded; in a parent's Variants
 * panel the selection is the variants themselves. Nothing is deleted.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { getVariantsForParent, logAdminEvents } from '../../services/firebase';
import { isStoreVariant, ungroupVariants } from '../../services/variantGrouping';
import { deriveIsVariant } from '../../utils/references';
import type { WatchBrand, WatchReference } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  brand: WatchBrand;
  selectedRefs: WatchReference[];
  onUngrouped: () => void;
}

export function UngroupVariantsDialog({ isOpen, onClose, brand, selectedRefs, onUngrouped }: Props) {
  const [variants, setVariants] = useState<WatchReference[] | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setVariants(null);
    let cancelled = false;
    (async () => {
      const direct = selectedRefs.filter((r) => deriveIsVariant(r.parentReferenceId));
      const parents = selectedRefs.filter((r) => !deriveIsVariant(r.parentReferenceId));
      const children = (await Promise.all(parents.map((p) => getVariantsForParent(brand.id, p.id)))).flat();
      const seen = new Set<string>();
      const all = [...direct, ...children].filter((v) => !seen.has(v.id) && seen.add(v.id));
      if (!cancelled) setVariants(all);
    })().catch((err) => {
      console.error('Could not load variants', err);
      toast.error('Could not load the variants to ungroup');
      if (!cancelled) setVariants([]);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedRefs, brand.id]);

  const storeCount = variants?.filter(isStoreVariant).length ?? 0;
  const parentCount = new Set(variants?.map((v) => v.parentReferenceId)).size;

  const handleApply = async () => {
    if (!variants || variants.length === 0) return;
    setApplying(true);
    try {
      const n = await ungroupVariants(brand.id, variants);
      const byParent = new Map<string, number>();
      for (const v of variants) byParent.set(v.parentReferenceId as string, (byParent.get(v.parentReferenceId as string) ?? 0) + 1);
      void logAdminEvents(
        [...byParent].map(([parentId, variantCount]) => ({
          name: 'variant_group_ungrouped' as const,
          params: { brandId: brand.id, parentId, variantCount },
        })),
      );
      toast.success(`Detached ${n} variant${n === 1 ? '' : 's'}`);
      onUngrouped();
      onClose();
    } catch (err) {
      console.error('Ungroup failed', err);
      toast.error(err instanceof Error ? err.message : 'Ungroup failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ungroup variants" size="lg">
      <div className="space-y-3 text-sm">
        {variants === null ? (
          <p className="text-gray-500">Loading variants…</p>
        ) : variants.length === 0 ? (
          <p className="text-gray-500">None of the selected references has variants.</p>
        ) : (
          <>
            <p className="text-gray-600">
              {variants.length} variant{variants.length === 1 ? '' : 's'} from {parentCount} parent
              {parentCount === 1 ? '' : 's'} will become top-level references. No reference is deleted; parents stay.
            </p>
            <ul className="max-h-[40vh] overflow-y-auto divide-y divide-gray-100 rounded border border-gray-200 text-xs">
              {variants.map((v) => (
                <li key={v.id} className="p-1.5">
                  <span className="font-mono text-gray-900">{v.reference}</span>
                  {v.model && <span className="text-gray-600"> · {v.model}</span>}
                </li>
              ))}
            </ul>
            {storeCount > 0 && (
              <p className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                {storeCount} of these came from a store import that groups them itself. The next import from that
                store links them back to their store parent (it fills an empty parent link). Turn on “Ignore Shopify
                parent-child relationships” (or switch off option expansion) on the source to keep them apart.
              </p>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleApply()} loading={applying} disabled={!variants?.length || applying}>
            Ungroup {variants?.length ?? 0}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
